import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useTranslation } from 'react-i18next';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';

import { MainMenuUserItem, PlusButton, FaqButton, Tooltip } from 'components';
import MainMenuSidebarMenu from './MainMenuSidebarMenu';
import {
  ABOUT_GUMB_MENU_ITEMS,
  HELP_SUPPORT_MENU_ITEMS,
} from './mainMenuSidebarMenus';
import MainMenuCommunityItem, {
  type MainMenuCommunityDisableReason,
} from './MainMenuCommunityItem';
import MainMenuDisabledCommunitiesGroup from './MainMenuDisabledCommunitiesGroup';
import { openModal, closeModal } from 'store/actionCreators/modal';
import { getAuthUser, getNotifications } from 'store/selectors/auth';
import { selectCurrentUserSubscription } from 'store/selectors/subscriptions';
import { getIsMenuOpen } from 'store/selectors/currentCommunity';
import { getCommunities } from 'store/selectors/communities';
import { doUpdateCommunitiesOrder } from 'store/actionCreators/communities';
import { checkUnreadNotifications } from 'utils/common';
import { CREATE_COMMUNITY, SUBSCRIPTION_OVER_POPUP } from 'constants/sidebars';
import { INFO_POPUP_V2 } from 'constants/popups';
import { CommunityRoles, CommunityListItem } from 'types';
import { Grid3x3Icon, Logo } from 'static';

import {
  StyledMainMenuBlock,
  StyledMainMenuTopBlock,
  StyledSeparator,
  StyledFaqButtonBlock,
  StyledMainMenuCommunities,
  StyledBottomMenu,
  StyledGrid3x3ButtonBlock,
  StyledProfileSection,
  StyledCommunitiesSection,
  StyledMenuActionSlot,
  StyledTooltipTriggerWrap,
  StyledLogoSlot,
  StyledLogoImage,
  MENU_ACTION_SIZE,
  MENU_ACTION_ICON_SIZE,
} from './styled';
import { PROFILE_ROUTES, ROUTES } from 'configs';
import { useNavigate } from 'react-router-dom';

// Helper to reorder array
function reorder(
  list: CommunityListItem[],
  startIndex: number,
  endIndex: number,
): CommunityListItem[] {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

function sidebarCommunityDisableReason(
  community: CommunityListItem,
): MainMenuCommunityDisableReason {
  const role = community.users?.[0]?.communityPermission?.role;
  if (role === CommunityRoles.BLOCKED) {
    return 'blocked';
  }
  if (community.subscriptionSlotActive === false) {
    return 'subscriptionSlotInactive';
  }
  return 'none';
}

const MainMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const communities = useSelector(getCommunities);
  const isMenuOpen = useSelector(getIsMenuOpen);
  const notifications = useSelector(getNotifications);
  const subscription = useSelector(selectCurrentUserSubscription);
  const userProfile = useSelector(getAuthUser);
  const tooltipProps = {
    tooltipStyle: {
      fontSize: 12,
      maxWidth: 200,
      whiteSpace: 'pre-wrap',
      lineBreak: 'normal',
    },
  };
  const handleRedirect = () => {
    dispatch(closeModal());
    navigate(`${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}`, {
      replace: true,
    });
  };

  const onAddCommunity = () => {
    if (!subscription?.id) {
      dispatch(
        openModal({
          type: SUBSCRIPTION_OVER_POPUP,
          data: { variant: 'noActivePlan' },
        }),
      );
      return;
    }
    if ((subscription.info?.communities || 0) < subscription.maxCommunities) {
      dispatch(openModal({ type: CREATE_COMMUNITY, placement: 'right' }));
    } else {
      dispatch(
        openModal({
          type: INFO_POPUP_V2,
          data: {
            headerText: t('community.createOverLimitTitle'),
            messageText: t('community.createOverLimitText'),
            defaultButtonText: 'Ok',
            type: 'warning',
            withoutCloseButton: true,
            onConfirm: () => handleRedirect(),
          },
        }),
      );
    }
  };

  const activeCommunities = communities.filter(
    (community) => sidebarCommunityDisableReason(community) === 'none',
  );
  const disabledCommunities = communities
    .filter((community) => sidebarCommunityDisableReason(community) !== 'none')
    .map((community) => ({
      community,
      disableReason: sidebarCommunityDisableReason(community),
    }));

  // Drag-and-drop for active communities only
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const newActiveOrder = reorder(
      activeCommunities,
      result.source.index,
      result.destination.index,
    );
    const newOrder = [
      ...newActiveOrder,
      ...disabledCommunities.map(({ community }) => community),
    ];

    // Save to localStorage for persistence
    localStorage.setItem(
      'communityOrder',
      JSON.stringify(newOrder.map((c) => c.id)),
    );

    // Get the moved community and its new position
    const movedCommunity = activeCommunities[result.source.index];
    const newPosition = result.destination.index;

    // Dispatch to Redux to update the order immediately and call API
    dispatch(
      doUpdateCommunitiesOrder.trigger({
        communities: newOrder,
        communityId: movedCommunity.id,
        end: newPosition,
      }),
    );
  };

  if (!userProfile) {
    return null;
  }

  return (
    <StyledMainMenuBlock isMenuOpen={isMenuOpen}>
      <StyledMainMenuTopBlock>
        <StyledProfileSection>
          <MainMenuUserItem
            image={userProfile.smallLogo || userProfile.logo}
            hasNotifications={checkUnreadNotifications(notifications)}
          />
        </StyledProfileSection>
        <StyledSeparator />
        <StyledCommunitiesSection>
          <PerfectScrollbar
            style={{
              width: '100%',
              maxHeight: 'calc(100svh - 340px)',
            }}
            className={'communities-list-scrollbar'}
            options={{
              wheelSpeed: 0.2,
              wheelPropagation: false,
              minScrollbarLength: 4,
            }}
          >
            <StyledMainMenuCommunities>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="communities-list">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {activeCommunities.map((community, index) => (
                        <Draggable
                          key={community.id}
                          draggableId={community.id}
                          index={index}
                        >
                          {(dragProvided) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                            >
                              <MainMenuCommunityItem
                                index={index}
                                isShowTooltip
                                title={community.name}
                                image={community.logo}
                                id={community.id}
                                disableReason="none"
                                invitesCount={community.requests?.length || 0}
                                userRole={
                                  community.users?.[0]?.communityPermission
                                    ?.role
                                }
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              {activeCommunities.length > 0 &&
                disabledCommunities.length > 0 && <StyledSeparator />}
            </StyledMainMenuCommunities>
          </PerfectScrollbar>
          <StyledMenuActionSlot>
            <PlusButton
              contained={false}
              muted={true}
              width={`${MENU_ACTION_SIZE}px`}
              tooltipStyle={tooltipProps.tooltipStyle}
              text={t('common.toolTipText.communityPlusIcon')}
              position="right-center"
              isVisible
              onClick={onAddCommunity}
            />
          </StyledMenuActionSlot>
          <StyledMenuActionSlot>
            <Tooltip
              {...tooltipProps}
              isVisible
              text={t('common.toolTipText.grid3x3')}
              position="right-center"
            >
              <StyledTooltipTriggerWrap>
                <StyledGrid3x3ButtonBlock
                  onClick={() =>
                    navigate(
                      `${ROUTES.PROFILE}/${PROFILE_ROUTES.COMMUNITIES}#matrix`,
                    )
                  }
                >
                  <Grid3x3Icon
                    color="#304659"
                    width={MENU_ACTION_ICON_SIZE}
                    height={MENU_ACTION_ICON_SIZE}
                  />
                </StyledGrid3x3ButtonBlock>
              </StyledTooltipTriggerWrap>
            </Tooltip>
          </StyledMenuActionSlot>
          <MainMenuDisabledCommunitiesGroup items={disabledCommunities} />
        </StyledCommunitiesSection>
        <StyledSeparator />
        <StyledLogoSlot>
          <MainMenuSidebarMenu
            panelId="about-gumb-menu-panel"
            titleKey="aboutGumbMenu.title"
            items={ABOUT_GUMB_MENU_ITEMS}
            triggerAriaLabel={t('aboutGumbMenu.title')}
            trigger={<StyledLogoImage src={Logo} alt="" />}
          />
        </StyledLogoSlot>
      </StyledMainMenuTopBlock>
      <StyledBottomMenu>
        <StyledFaqButtonBlock>
          <MainMenuSidebarMenu
            panelId="help-support-menu-panel"
            titleKey="helpSupportMenu.title"
            items={HELP_SUPPORT_MENU_ITEMS}
            triggerAriaLabel={t('helpSupportMenu.title')}
            trigger={<FaqButton />}
          />
        </StyledFaqButtonBlock>
      </StyledBottomMenu>
    </StyledMainMenuBlock>
  );
};

MainMenu.defaultProps = {
  communities: [],
  userProfile: {},
  notifications: [],
};

export default MainMenu;
